import { h, Host } from "@stencil/core";
import { theme } from "@eventstore-ui/theme";
import { closest } from "@eventstore-ui/utils";
/**
 * A button.
 * @slot before - Placed before the main content with correct padding.
 * @slot after - Placed after the main content with correct padding.
 * @part button - The internal button element.
 * @part inner - The inner span, wrapping the default slot.
 */
export class Button {
  constructor() {
    this.variant = 'default';
    this.disabled = undefined;
    this.type = 'button';
  }
  handleKeyUp(e) {
    if (e.key === 'Enter' || e.key === ' ') {
      this.host.dispatchEvent(new MouseEvent('click'));
    }
  }
  handleClick(e) {
    if (this.disabled) {
      e.preventDefault();
      e.stopPropagation();
      return false;
    }
    if (this.type === 'submit') {
      const form = closest(this.host, 'form');
      if (form) {
        e.preventDefault();
        const fakeSubmit = document.createElement('button');
        fakeSubmit.type = 'submit';
        fakeSubmit.style.display = 'none';
        form.appendChild(fakeSubmit);
        fakeSubmit.click();
        fakeSubmit.remove();
      }
    }
  }
  connectedCallback() {
    if (this.host.hasAttribute('tabindex')) {
      this.tabindex = this.host.getAttribute('tabindex');
    }
  }
  render() {
    return (h(Host, { "high-contrast": theme.isHighContrast(), dark: theme.isDark() }, h("button", { tabindex: this.tabindex, type: this.type, disabled: this.disabled, part: 'button' }, h("slot", { name: 'before' }), h("span", { part: 'inner' }, h("slot", null)), h("slot", { name: 'after' }))));
  }
  static get is() { return "es-button"; }
  static get encapsulation() { return "shadow"; }
  static get originalStyleUrls() {
    return {
      "$": ["../button.css"]
    };
  }
  static get styleUrls() {
    return {
      "$": ["../button.css"]
    };
  }
  static get properties() {
    return {
      "variant": {
        "type": "string",
        "mutable": false,
        "complexType": {
          "original": "ButtonVariant",
          "resolved": "\"cancel\" | \"default\" | \"delete\" | \"filled\" | \"link\" | \"minimal\" | \"outline\"",
          "references": {
            "ButtonVariant": {
              "location": "import",
              "path": "../types",
              "id": "src/components/buttons/types.ts::ButtonVariant"
            }
          }
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": "Which styling variant to use."
        },
        "attribute": "variant",
        "reflect": true,
        "defaultValue": "'default'"
      },
      "disabled": {
        "type": "boolean",
        "mutable": false,
        "complexType": {
          "original": "boolean",
          "resolved": "boolean | undefined",
          "references": {}
        },
        "required": false,
        "optional": true,
        "docs": {
          "tags": [],
          "text": "If the button is disabled. Prevents the user from interacting with the button: it cannot be pressed or focused."
        },
        "attribute": "disabled",
        "reflect": true
      },
      "type": {
        "type": "string",
        "mutable": false,
        "complexType": {
          "original": "string",
          "resolved": "string",
          "references": {}
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": "The default behavior of the button."
        },
        "attribute": "type",
        "reflect": false,
        "defaultValue": "'button'"
      }
    };
  }
  static get elementRef() { return "host"; }
  static get listeners() {
    return [{
        "name": "keyup",
        "method": "handleKeyUp",
        "target": undefined,
        "capture": false,
        "passive": false
      }, {
        "name": "click",
        "method": "handleClick",
        "target": undefined,
        "capture": true,
        "passive": false
      }];
  }
}
//# sourceMappingURL=es-button.js.map
